/** A document's history and where it stands in its workflow - EXP-19's "workflow and history".
 *
 * The page for a document showed the file, the figures it became and the text read from it, and
 * nothing of what had happened to it. The ledger already holds that: every `document.*` act names the
 * document as its object, so `GET /activity?record_id=` answers it with the chain's own sentences
 * (the same read the activity record's History tab makes). This lists them oldest first, each with the
 * ledger entry that holds it.
 *
 * **The workflow is the state, and the way into its approvals.** There is no workflow engine and the
 * panel does not pretend to one (ADR-0010 is accepted and unbuilt): a document moves by its
 * transition table, `AWAITING_REVIEW` is the only route into `VERIFIED`, and who may act on it is
 * the Approvals tab of this same page. The panel says the state in words and links there.
 *
 * **A refusal is not an empty history.** A Contributor or a Viewer does not read the chain's acts, and
 * neither does an Auditor engaged for a period or particular companies (ADR-0048); the request is not
 * made for them, and the panel says that, rather than drawing "nothing happened" under a verified
 * document. Names are never shown: an act carries its actor's kind, and the sentence is the server's.
 */

import { Link } from "@tanstack/react-router";

import { useRecordActs } from "../api/activity";
import { useMe } from "../api/auth";
import { useReader } from "../api/profile";
import { Refused } from "../api/refusal";
import { ReadFailed } from "../components/Loaded";
import { Empty, Panel } from "../components/Panel";
import { StatusChip } from "../components/ui";
import { stateWord } from "../lib/documentStages";
import { instantDate } from "../lib/format";
import { keepSearch } from "./search";
import type { ScreenSearch } from "./search";
import styles from "./Screen.module.css";
import own from "./Record.module.css";

/** The state in the one table's word (`lib/documentStages.ts`, G-Wauditor-13); `expired` is the one
 * state the table does not carry, because the retention path is not built. */
function stateWords(state: string): string {
  return state === "expired" ? "Past its retention period" : stateWord(state);
}

export function DocumentHistory({ documentId, state }: { documentId: string; state: string }) {
  const reader = useReader();
  const { data: me } = useMe();
  // Not asked for when the server refuses it by design (the record page's History tab draws the
  // same line): a request made only to be answered 403 is a failed call in every log.
  const role = me?.role;
  const mayReadActs =
    (role === "admin" || role === "approver" || role === "auditor") && (me?.engagement ?? null) === null;
  const acts = useRecordActs(documentId, mayReadActs);
  const refused =
    me != null &&
    (!mayReadActs ||
      (acts.error instanceof Refused && (acts.error.status === 403 || acts.error.status === 401)));
  const rows = [...(acts.data?.rows ?? [])].reverse();

  return (
    <Panel title="History and workflow" column="1 / 13" sub={stateWords(state)}>
      <p className={styles.note} data-testid="document-workflow">
        <StatusChip tone={state === "verified" ? "ok" : state === "rejected" ? "crit" : "info"} size="sm">
          {stateWords(state)}
        </StatusChip>{" "}
        <Link
          className={styles.fixLink}
          to="/records/$recordId"
          params={{ recordId: documentId }}
          search={(prev: Partial<ScreenSearch>) => ({ ...keepSearch(prev), tab: "approvals" })}
        >
          Approvals and tasks
        </Link>
      </p>
      {refused ? (
        <Empty>Your role does not read the ledger&apos;s acts, so they are not listed here.</Empty>
      ) : acts.error ? (
        <ReadFailed
          of="This document's history"
          error={acts.error}
          onRetry={() => void acts.refetch()}
        />
      ) : acts.isPending ? (
        <Empty>Loading…</Empty>
      ) : rows.length === 0 ? (
        <Empty>Nothing has been recorded about this document.</Empty>
      ) : (
        <ul className={own.docFigures} data-testid="document-history">
          {rows.map((row) => (
            <li key={row.seq} className={own.docFigure} data-testid="document-history-row">
              <span className={own.docFigureMeta}>{instantDate(row.recorded_at, reader)}</span>
              <span>{row.sentence}</span>
              <Link
                className={styles.fixLink}
                to="/ledger/$seq"
                params={{ seq: String(row.seq) }}
                search={keepSearch}
              >
                Entry {row.seq}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}
